<script setup lang="ts">
/**
 * "Not in FSD" — the visible half of C15's reverse-conformance audit.
 *
 * `docs/FSD_UI_EXTRANEOUS.md` lists every screen with no feature specification
 * anywhere in `docs/FSD x ERP Cloud 1.docx`, and `nav/parts/*` marks each one
 * `fsdStatus: 'extraneous'`. Until this component, nothing on screen read that
 * marker, so a reviewer walking the product had no way to tell an FSD screen
 * from a carried-over one. It is shown wherever a flagged screen is offered —
 * the app sidebar, the launcher, the home screen's lists, the palette — and on
 * the flagged screen's own page header.
 *
 * Deliberately quiet (amber outline, small type): it is an annotation for the
 * people reviewing scope, not an error the person doing the work must act on.
 * The text is visible, never icon-only, so it needs no tooltip to be
 * understood; the `title` adds the why for whoever hovers.
 */

withDefaults(
  defineProps<{
    /** `dot` for the icon-only collapsed sidebar; `pill` everywhere else. */
    variant?: 'pill' | 'dot'
  }>(),
  { variant: 'pill' },
)
</script>

<template>
  <span
    v-if="variant === 'pill'"
    class="fsd-badge"
    :title="$t('shell.fsd.notInFsdHint')"
  >{{ $t('shell.fsd.notInFsd') }}</span>
  <span v-else class="fsd-badge-dot" :title="$t('shell.fsd.notInFsdHint')">
    <span class="sr-only">{{ $t('shell.fsd.notInFsd') }}</span>
  </span>
</template>

<style>
.fsd-badge {
  display: inline-flex;
  flex: none;
  align-items: center;
  block-size: 16px;
  padding-inline: 5px;
  border: 1px solid var(--erpx-color-warning-border);
  border-radius: var(--erpx-radius-full);
  background: var(--erpx-color-warning-surface);
  color: var(--erpx-color-warning-text);
  font-size: 10px;
  font-weight: var(--erpx-weight-medium);
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;
}

.fsd-badge-dot {
  display: inline-block;
  flex: none;
  inline-size: 6px;
  block-size: 6px;
  border-radius: var(--erpx-radius-full);
  background: var(--erpx-color-warning-solid);
}
</style>
